import { useState } from 'react';
import { Rss, Check, ExternalLink } from 'lucide-react';
import { useAuthStore } from '../store/useStore';
import { feeds as feedsApi } from '../lib/api';
import toast from 'react-hot-toast';

interface LinkData {
  url: string;
  feedUrl?: string;
  title?: string;
}

interface CommentLinkEmbedProps {
  links: LinkData[];
}

export default function CommentLinkEmbed({ links }: CommentLinkEmbedProps) {
  const token = useAuthStore((state) => state.token)!;
  const [subscribedUrls, setSubscribedUrls] = useState<Set<string>>(new Set());
  const [subscribingUrls, setSubscribingUrls] = useState<Set<string>>(new Set());

  // Only show links that have feeds
  const linksWithFeeds = links.filter(link => link.feedUrl);

  if (linksWithFeeds.length === 0) {
    return null;
  }

  async function handleSubscribe(link: LinkData) {
    if (!link.feedUrl) return;

    setSubscribingUrls(prev => new Set(prev).add(link.feedUrl!));

    try {
      await feedsApi.subscribe(token, { url: link.feedUrl });
      setSubscribedUrls(prev => new Set(prev).add(link.feedUrl!));
      toast.success(`Subscribed to ${link.title || 'feed'}!`);
    } catch (error: any) {
      if (error.message?.includes('already subscribed')) {
        setSubscribedUrls(prev => new Set(prev).add(link.feedUrl!));
        toast.error('Already subscribed to this feed');
      } else {
        toast.error('Failed to subscribe to feed');
      }
    } finally {
      setSubscribingUrls(prev => {
        const newSet = new Set(prev);
        newSet.delete(link.feedUrl!);
        return newSet;
      });
    }
  }

  function getDomain(url: string): string {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.replace('www.', '');
    } catch {
      return url;
    }
  }

  function getFaviconUrl(url: string): string {
    try {
      const urlObj = new URL(url);
      return `https://www.google.com/s2/favicons?domain=${urlObj.hostname}&sz=32`;
    } catch {
      return '';
    }
  }

  return (
    <div className="mt-3 space-y-2">
      {linksWithFeeds.map((link, index) => {
        const isSubscribed = subscribedUrls.has(link.feedUrl!);
        const isSubscribing = subscribingUrls.has(link.feedUrl!);
        const domain = getDomain(link.url);
        const faviconUrl = getFaviconUrl(link.url);

        return (
          <div
            key={index}
            className="flex items-center justify-between p-3 bg-gray-50 border border-edge rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {/* Favicon */}
              <img
                src={faviconUrl}
                alt=""
                className="w-6 h-6 rounded flex-shrink-0"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />

              {/* Link info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-ink hover:text-primary-600 truncate flex items-center gap-1"
                  >
                    {link.title || domain}
                    <ExternalLink className="w-3 h-3 flex-shrink-0" />
                  </a>
                </div>
                <div className="text-xs text-ink-muted truncate">{domain}</div>
              </div>
            </div>

            {/* Subscribe button */}
            <button
              onClick={() => handleSubscribe(link)}
              disabled={isSubscribed || isSubscribing}
              className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-md transition-colors whitespace-nowrap ${
                isSubscribed
                  ? 'bg-green-100 text-green-700 cursor-default'
                  : 'bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50'
              }`}
            >
              {isSubscribed ? (
                <>
                  <Check className="w-4 h-4" />
                  Subscribed
                </>
              ) : (
                <>
                  <Rss className="w-4 h-4" />
                  {isSubscribing ? 'Subscribing...' : 'Subscribe'}
                </>
              )}
            </button>
          </div>
        );
      })}
    </div>
  );
}
